"use client";
import { FC, useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { RiMenu3Line, RiCloseLine } from "react-icons/ri";
import { ProfileInitials } from "@/components/dashboard/ProfileIntials";
import { useLogout } from "@/api/hook/useAuth";
import { useCurrentStudent } from "@/api/hook/useDashboard";
import { RichTextDisplay } from "../app/RichTextDisplay";
import apiClient from "@/api/config";
import { ProfileImage } from "../dashboard/ProfileImage";
import { useCategories } from "@/api/hook/useCategories";

interface SearchResult {
  id: number;
  courseName: string;
  categoryName: string;
  courseDescription: string;
  amount: number;
  free: boolean;
}

const Header: FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [mobileSearchTerm, setMobileSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [mobileSearchResults, setMobileSearchResults] = useState<
    SearchResult[]
  >([]);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [showMobileSearchModal, setShowMobileSearchModal] =
    useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isMobileSearching, setIsMobileSearching] = useState<boolean>(false);
  const [showProfileDropdown, setShowProfileDropdown] =
    useState<boolean>(false);
  const [showCoursesDropdown, setShowCoursesDropdown] =
    useState<boolean>(false);

  const searchModalRef = useRef<HTMLDivElement>(null);
  const mobileSearchModalRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const coursesDropdownRef = useRef<HTMLDivElement>(null);
  const { data: categories } = useCategories();
  const pathname = usePathname();
  const router = useRouter();

  const { logout } = useLogout();

  // Logged-in student comes from the query cache (null when logged out)
  const student = useCurrentStudent();

  // Handle clicks outside dropdowns and modals
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }

      if (
        coursesDropdownRef.current &&
        !coursesDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCoursesDropdown(false);
      }

      if (
        searchModalRef.current &&
        !searchModalRef.current.contains(event.target as Node)
      ) {
        setShowSearchModal(false);
      }

      if (
        mobileSearchModalRef.current &&
        !mobileSearchModalRef.current.contains(event.target as Node)
      ) {
        setShowMobileSearchModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    return pathname.startsWith(path);
  };

  const dropdownLink = (categories ?? [])
    .filter((cat) => cat.categoryName !== "In progress...")
    .map((cat) => ({
      id: cat.id,
      title: cat.categoryName,
      link: `https://student.laplagemetaverse.org/courses/${cat.id}`,
    }));

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Desktop search function
  const searchCourses = async (searchWord: string) => {
    if (searchWord.length < 2) {
      setSearchResults([]);
      setShowSearchModal(false);
      return;
    }

    setIsSearching(true);
    try {
      const { data } = await apiClient.get(
        `/public/searchCourses/${searchWord}`,
      );
      setSearchResults(data);
      setShowSearchModal(true);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Mobile search function
  const searchCoursesMobile = async (searchWord: string) => {
    if (searchWord.length < 2) {
      setMobileSearchResults([]);
      setShowMobileSearchModal(false);
      return;
    }

    setIsMobileSearching(true);
    try {
      const { data } = await apiClient.get(
        `/public/searchCourses/${searchWord}`,
      );
      setMobileSearchResults(data);
      setShowMobileSearchModal(true);
    } catch (error) {
      console.error("Mobile search failed:", error);
    } finally {
      setIsMobileSearching(false);
    }
  };

  // Desktop search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCourses(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Mobile search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCoursesMobile(mobileSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [mobileSearchTerm]);

  const handleCourseClick = (courseId: number, isMobile: boolean = false) => {
    router.push(`/course/${courseId}`);
    if (isMobile) {
      setShowMobileSearchModal(false);
      setMobileSearchTerm("");
    } else {
      setShowSearchModal(false);
      setSearchTerm("");
    }
  };

  return (
    <nav className="w-full bg-[#FFF] sticky top-0 left-0 shadow-sm z-50">
      <div className="xl:container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo and Search Section */}
          <div className="flex items-center gap-4 flex-1 md:gap-8">
            <Link href="/" className="flex-shrink-0">
              <Image
                className="w-24 md:w-32 h-auto"
                alt="logo"
                src="/logo.svg"
                width={128}
                height={40}
                priority
              />
            </Link>

            {/* Desktop Search Bar */}
            <div className="hidden md:block relative flex-1 max-w-2xl">
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="What do you want to learn?"
                className="w-full rounded-md py-2 pl-4 pr-10 text-base border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <div className="absolute inset-y-0 right-3 flex items-center">
                <HiMagnifyingGlass className="h-5 w-5 text-[#9CA3AF]" />
              </div>

              {/* Desktop Search Results Modal */}
              {showSearchModal && (
                <div
                  ref={searchModalRef}
                  className="absolute mt-2 w-full bg-[#FFF] rounded-lg shadow-lg border border-gray-200 max-h-[70vh] overflow-y-auto z-50"
                >
                  {isSearching ? (
                    <div className="p-4 text-center text-[#4B5563]">
                      Searching...
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          onClick={() => handleCourseClick(result.id)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="flex flex-col gap-1">
                            <h4 className="font-medium text-primary">
                              {result.courseName}
                            </h4>
                            <p className="text-sm text-[#4B5563]">
                              {result.categoryName}
                            </p>
                            <p className="text-sm text-[#6B7280] line-clamp-2">
                              <RichTextDisplay
                                content={result.courseDescription}
                              />
                            </p>
                            <span className="text-sm font-medium text-primary">
                              {result.free
                                ? "Free"
                                : `₦${result.amount.toLocaleString()}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchTerm.length >= 2 ? (
                    <div className="p-4 text-center text-[#4B5563]">
                      No courses found
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {/* Courses Dropdown */}
            <div className="relative" ref={coursesDropdownRef}>
              <button
                onClick={() => setShowCoursesDropdown(!showCoursesDropdown)}
                className={`inline-flex items-center gap-2 text-base font-medium ${
                  isActive("/courses") ? "text-[#8253C3]" : "text-gray-700"
                }`}
              >
                Courses
                <svg
                  className={`w-4 h-4 transition-transform ${
                    showCoursesDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="m6 9 6 6 6-6" strokeWidth="2" />
                </svg>
              </button>

              {/* Courses Dropdown Menu */}
              {showCoursesDropdown && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-[#FFF] ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {dropdownLink.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => {
                          router.push(item.link);
                          setShowCoursesDropdown(false);
                        }}
                        className={`block px-4 py-2 text-sm cursor-pointer ${
                          pathname === item.link
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-[#374151] hover:bg-[#F3F4F6]"
                        }`}
                      >
                        {item.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {student ? (
              // Show profile when logged in
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-3 text-primary hover:text-primary/90"
                >
                  <ProfileImage
                    studentId={student.id}
                    firstName={student.firstName}
                    className="w-10 h-10"
                  />
                  <span className="font-medium">
                    {student.firstName} {student.lastName}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      showProfileDropdown ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="m6 9 6 6 6-6" strokeWidth="2" />
                  </svg>
                </button>

                {/* Profile Dropdown */}
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-[#FFF] ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <Link
                        href="/dashboard"
                        className="block px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6]"
                      >
                        Dashboard
                      </Link>
                      <Link
                        href="/dashboard/profile"
                        className="block px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6]"
                      >
                        My Profile
                      </Link>
                      <div
                        onClick={() => logout()}
                        className="block px-4 py-2 text-sm text-red-700 hover:bg-red-100 cursor-pointer"
                      >
                        Log out
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Show login/register when not logged in
              <>
                <Link
                  href="/sign-in"
                  className="text-base font-medium text-[#374151] hover:text-primary"
                >
                  Log In
                </Link>
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center px-4 py-2 text-base font-medium text-[#FFF] bg-primary rounded-md hover:bg-primary/90"
                >
                  Register for FREE
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700"
          >
            {open ? (
              <RiCloseLine className="h-6 w-6" />
            ) : (
              <RiMenu3Line className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Search Bar */}
        <div className="md:hidden pb-4 relative">
          <div className="relative">
            <input
              type="text"
              name="mobileSearch"
              value={mobileSearchTerm}
              onChange={(e) => setMobileSearchTerm(e.target.value)}
              placeholder="What do you want to learn?"
              className="w-full rounded-md py-2 pl-4 pr-10 text-base border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <div className="absolute inset-y-0 right-3 flex items-center">
              <HiMagnifyingGlass className="h-5 w-5 text-[#9CA3AF]" />
            </div>
          </div>

          {/* Mobile Search Results Modal */}
          {showMobileSearchModal && (
            <div
              ref={mobileSearchModalRef}
              className="absolute mt-2 w-full bg-[#FFF] rounded-lg shadow-lg border border-gray-200 max-h-[70vh] overflow-y-auto z-50"
            >
              {isMobileSearching ? (
                <div className="p-4 text-center text-[#4B5563]">
                  Searching...
                </div>
              ) : mobileSearchResults.length > 0 ? (
                <div className="py-2">
                  {mobileSearchResults.map((result) => (
                    <div
                      key={result.id}
                      onClick={() => handleCourseClick(result.id, true)}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex flex-col gap-1">
                        <h4 className="font-medium text-primary">
                          {result.courseName}
                        </h4>
                        <p className="text-sm text-[#4B5563]">
                          {result.categoryName}
                        </p>
                        <p className="text-sm text-[#6B7280] line-clamp-2">
                          <RichTextDisplay content={result.courseDescription} />
                        </p>
                        <span className="text-sm font-medium text-primary">
                          {result.free
                            ? "Free"
                            : `₦${result.amount.toLocaleString()}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : mobileSearchTerm.length >= 2 ? (
                <div className="p-4 text-center text-[#4B5563]">
                  No courses found
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`md:hidden ${
            open ? "block" : "hidden"
          } border-t border-gray-200`}
        >
          <div className="pt-2 pb-4 space-y-1">
            {/* Mobile Courses Dropdown */}
            <div className="px-4">
              <button
                onClick={() => setShowCoursesDropdown(!showCoursesDropdown)}
                className={`flex items-center justify-center w-full py-2 text-base font-medium ${
                  isActive("/courses") ? "text-primary" : "text-gray-700"
                }`}
              >
                Courses
                <svg
                  className={`ml-2 w-4 h-4 transition-transform ${
                    showCoursesDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="m6 9 6 6 6-6" strokeWidth="2" />
                </svg>
              </button>
              {showCoursesDropdown && (
                <div className="mt-2 space-y-1">
                  {dropdownLink.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        router.push(item.link);
                        setShowCoursesDropdown(false);
                        setOpen(false);
                      }}
                      className={`block px-4 py-2 text-sm cursor-pointer rounded ${
                        pathname === item.link
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-[#374151] hover:bg-[#F3F4F6]"
                      }`}
                    >
                      {item.title}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {student ? (
              <>
                <div className="flex items-center gap-3 px-4 py-2">
                  <ProfileInitials name={student.firstName} />
                  <span className="font-medium text-primary">
                    {student.firstName} {student.lastName}
                  </span>
                </div>
                <Link
                  href="/dashboard"
                  className="block px-4 py-2 text-base text-[#374151] hover:text-primary"
                >
                  Dashboard
                </Link>
                <Link
                  href="/dashboard/profile"
                  className="block px-4 py-2 text-base text-[#374151] hover:text-primary"
                >
                  My Profile
                </Link>
                <div
                  onClick={() => logout()}
                  className="block px-4 py-2 text-base text-red-700 hover:bg-red-100 cursor-pointer"
                >
                  Log out
                </div>
              </>
            ) : (
              <>
                <Link
                  href="/sign-in"
                  className="block px-4 py-2 text-base font-medium text-[#374151] text-center"
                >
                  Log In
                </Link>
                <Link
                  href="/sign-up"
                  className="block mx-4 mt-2 text-center px-4 py-2 text-base font-medium text-[#FFF] bg-primary rounded-md"
                >
                  Register for FREE
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
